import { useState, useCallback, useMemo, useEffect } from 'react';
import { Search, Filter, SlidersHorizontal, Download, ScrollText } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { LiveConsoleBreadcrumbs } from '../LiveConsoleBreadcrumbs';
import {
  Input, Button, Skeleton,
  Popover, PopoverTrigger, PopoverContent,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuCheckboxItem,
} from '@vnetwork/ui/primitives';
import {
  DataTable, EmptyState, Pagination, FilterBuilder, StatusPill,
  type ColumnDef, type FilterField, type FilterCondition,
} from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import { useLmsAudits } from '@/api/media/live-audits';
import type { LmsAuditLog, LmsAuditActionFilter } from '@/api/media/live-audits-types';
import { showingRange } from '../../components/RowMenu';
import { useDebounce } from '../../shared';
import styles from './AuditList.module.css';

const PER_PAGE = 20;

// StatusPill tone subset used in this table.
type SafeBadgeTone = 'success' | 'warning' | 'danger' | 'neutral';

// HTTP status code IS a real status dimension, so a colored tone is warranted.
function httpTone(code: number): SafeBadgeTone {
  if (code >= 500) return 'danger';
  if (code >= 400) return 'warning';
  if (code >= 200) return 'success';
  return 'neutral';
}

// Columns the user can toggle via "Display options" (ID stays fixed as the primary key).
const TOGGLEABLE = [
  { id: 'time', label: 'Time' },
  { id: 'account', label: 'Account' },
  { id: 'method', label: 'Method' },
  { id: 'endpoint', label: 'Endpoint' },
  { id: 'status', label: 'Status' },
  { id: 'client_ip', label: 'Client IP' },
  { id: 'response_time', label: 'Response Time' },
  { id: 'request_id', label: 'Request ID' },
  { id: 'payload', label: 'Payload' },
];
const DEFAULT_VISIBLE: Record<string, boolean> = {
  time: true, account: true, method: true, endpoint: true, status: true,
  client_ip: true, response_time: true, request_id: true, payload: true,
};

/** Download the given audit rows as a CSV file. */
function exportCsv(rows: LmsAuditLog[]) {
  const head = ['id', 'time', 'account', 'method', 'endpoint', 'status', 'client_ip', 'response_time', 'request_id', 'payload'];
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const body = rows
    .map((r) =>
      [r.id, r.spot_time_str, r.identity, r.http_method, r.endpoint, r.http_code, r.client_ip, r.response_time, r.request_id, r.request_payload]
        .map(esc)
        .join(','),
    )
    .join('\n');
  const blob = new Blob([`${head.join(',')}\n${body}`], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `live-audit-${rows.length}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export interface LiveAuditProps {
  serviceId: string;
}

/**
 * Paginated audit log. Search = account; Filters popover = action (enum) + date
 * range; both wire to the server query. Columns are toggleable via Display options.
 */
export function LiveAudit({ serviceId }: LiveAuditProps) {
  const { show } = useToast();

  const [page, setPage] = useState(1);
  const [accountInput, setAccountInput] = useState('');
  const account = useDebounce(accountInput, 400);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);

  // Reset to page 1 whenever the debounced account value changes.
  useEffect(() => { setPage(1); }, [account]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [visible, setVisible] = useState<Record<string, boolean>>(DEFAULT_VISIBLE);

  // Active scope derived from the FilterBuilder conditions.
  const collect = (field: string) =>
    conditions
      .filter((c) => c.field === field)
      .flatMap((c) => (c.value || '').split(','))
      .map((s) => s.trim())
      .filter(Boolean);
  const action = (collect('action')[0] as LmsAuditActionFilter) || 'all';

  // Date bounds from the date condition (after | before | between).
  const dateCond = conditions.find((c) => c.field === 'date' && c.value.trim());
  let startDate: string | undefined;
  let endDate: string | undefined;
  if (dateCond) {
    const [a = '', b = ''] = dateCond.value.split(',');
    if (dateCond.operator === 'after') startDate = a || undefined;
    else if (dateCond.operator === 'before') endDate = a || undefined;
    else if (dateCond.operator === 'between') {
      startDate = a || undefined;
      endDate = b || undefined;
    }
  }

  const filterFields: FilterField[] = [
    {
      value: 'action',
      label: 'Action',
      input: 'select',
      operators: [{ value: 'equals', label: 'is' }],
      options: [{ value: 'stream', label: 'Stream' }],
    },
    { value: 'date', label: 'Date', input: 'date' },
  ];

  const { data, isLoading, isFetching } = useLmsAudits(
    serviceId,
    { page, perPage: PER_PAGE, account: account || undefined, startDate, endDate, action },
  );

  const logs = data?.list ?? [];
  const totalPages = data?.total_page_number ?? 1;
  const total = data?.total_count ?? 0;
  const loading = isLoading;

  const handleCopy = useCallback((text: string) => {
    navigator.clipboard.writeText(text).then(() => show({ tone: 'info', title: 'Copied to clipboard' }));
  }, [show]);

  const columns = useMemo<ColumnDef<LmsAuditLog, unknown>[]>(() => {
    const all: Record<string, ColumnDef<LmsAuditLog, unknown>> = {
      time: {
        id: 'time',
        header: 'Time',
        accessorKey: 'spot_time_str',
        cell: ({ row }) => <span className={styles.timeCell}>{row.original.spot_time_str}</span>,
      },
      account: { id: 'account', header: 'Account', accessorKey: 'identity' },
      method: { id: 'method', header: 'Method', accessorKey: 'http_method' },
      endpoint: { id: 'endpoint', header: 'Endpoint', accessorKey: 'endpoint' },
      status: {
        id: 'status',
        header: 'Status',
        accessorKey: 'http_code',
        cell: ({ row }) => <StatusPill tone={httpTone(row.original.http_code)}>{row.original.http_code}</StatusPill>,
      },
      client_ip: { id: 'client_ip', header: 'Client IP', accessorKey: 'client_ip' },
      response_time: { id: 'response_time', header: 'Response Time', accessorKey: 'response_time' },
      request_id: {
        id: 'request_id',
        header: 'Request ID',
        accessorKey: 'request_id',
        cell: ({ row }) => {
          const text = row.original.request_id ?? '';
          return (
            <button
              type="button"
              className={styles.copyableCell}
              title="Click to copy request ID"
              aria-label={`Copy request ID ${text}`}
              disabled={!text}
              onClick={() => handleCopy(text)}
            >
              {text || '—'}
            </button>
          );
        },
      },
      payload: {
        id: 'payload',
        header: 'Payload',
        accessorKey: 'request_payload',
        cell: ({ row }) => {
          const text = row.original.request_payload ?? '';
          return (
            <button
              type="button"
              className={styles.payloadCell}
              title={text || '—'}
              aria-label="Copy request payload"
              disabled={!text}
              onClick={() => handleCopy(text)}
            >
              {text || '—'}
            </button>
          );
        },
      },
    };
    const idCol: ColumnDef<LmsAuditLog, unknown> = {
      id: 'id',
      header: 'ID',
      accessorKey: 'id',
      cell: ({ row }) => <span className={styles.idCell}>{row.original.id}</span>,
    };
    return [idCol, ...TOGGLEABLE.filter((c) => visible[c.id]).map((c) => all[c.id])];
  }, [handleCopy, visible]);

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<LiveConsoleBreadcrumbs trail={[{ label: 'Audit Log' }]} />}
        title={`Audit Log${total ? ` (${total})` : ''}`}
        description="Account-level API activity for this service."
      />
      <Toolbar
        start={
          <Input
            startSlot={<Search size={15} />}
            placeholder="Search by account"
            aria-label="Account"
            value={accountInput}
            onChange={(e) => setAccountInput(e.target.value)}
            wrapperClassName={styles.searchInput}
          />
        }
        end={
          <>
            <Popover open={filterOpen} onOpenChange={setFilterOpen}>
              <PopoverTrigger asChild>
                <Button variant="secondary" startIcon={<Filter size={15} />}>
                  Filters{conditions.length ? ` (${conditions.length})` : ''}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className={styles.filterPopoverContent}>
                <FilterBuilder
                  fields={filterFields}
                  defaultValue={conditions.length ? conditions : undefined}
                  onApply={(c) => {
                    setConditions(c.filter((x) => x.value.trim()));
                    setPage(1);
                    setFilterOpen(false);
                  }}
                  onClose={() => setFilterOpen(false)}
                />
              </PopoverContent>
            </Popover>
            {conditions.length > 0 && (
              <Button variant="ghost" onClick={() => { setConditions([]); setPage(1); }}>
                Clear
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>
                  Display options
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                {TOGGLEABLE.map((c) => (
                  <DropdownMenuCheckboxItem
                    key={c.id}
                    checked={visible[c.id]}
                    onCheckedChange={(v) => setVisible((prev) => ({ ...prev, [c.id]: v === true }))}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {c.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="secondary"
              startIcon={<Download size={15} />}
              disabled={logs.length === 0}
              onClick={() => exportCsv(logs)}
            >
              Export
            </Button>
          </>
        }
      />

      {loading ? (
        <div className={styles.skeletonGrid}>
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="rect" height={44} />)}
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={logs}
            getRowId={(r) => String(r.id)}
            emptyState={
              <EmptyState
                icon={<ScrollText size={20} />}
                title="No audit logs"
                description="No records match your current search or filters."
              />
            }
            footer={total ? showingRange(page, PER_PAGE, logs.length, total) : undefined}
          />

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <Pagination
                page={page}
                pageCount={totalPages}
                onPageChange={setPage}
                summary={isFetching ? 'Loading…' : undefined}
              />
            </div>
          )}
        </>
      )}
    </Container>
  );
}
